import { addMonths, format } from "date-fns";
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useFormatting } from "@/hooks/useFormatting";
import { useTerminology } from "@/hooks/useTerminology";

interface SecureFinancingWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete: (eventData: any) => void;
}

export function SecureFinancingWizard({
  open,
  onOpenChange,
  onComplete,
}: SecureFinancingWizardProps) {
  const { getCashMovementTerms, getScenarioTerm } = useTerminology();
  const { formatCurrency } = useFormatting();
  const [step, setStep] = useState(1);

  // Step 1: Basic Information
  const [financingType, setFinancingType] = useState<
    "loan" | "overdraft" | "equity" | "grant"
  >("loan");
  const [amount, setAmount] = useState("100000");
  const [receiveDate, setReceiveDate] = useState<Date>(new Date(2026, 2, 1));

  // Step 2: Terms
  const [interestRate, setInterestRate] = useState("5");
  const [termMonths, setTermMonths] = useState("60");
  const [repaymentSchedule, setRepaymentSchedule] = useState<
    "monthly" | "quarterly"
  >("monthly");
  const [equityNote, setEquityNote] = useState("");
  const [grantConditions, setGrantConditions] = useState("");

  // Step 3: Costs
  const [arrangementFee, setArrangementFee] = useState("2000");
  const [legalFees, setLegalFees] = useState("1500");

  // Step 4: Review
  const [description, setDescription] = useState("");

  const handleNext = () => setStep(step + 1);
  const handleBack = () => setStep(step - 1);

  const handleComplete = () => {
    const eventData = {
      type: "secure_financing",
      financingType,
      amount: parseFloat(amount),
      receiveDate,
      interestRate: ["loan", "overdraft"].includes(financingType)
        ? parseFloat(interestRate)
        : 0,
      termMonths: ["loan", "overdraft"].includes(financingType)
        ? parseInt(termMonths)
        : 0,
      repaymentSchedule: ["loan", "overdraft"].includes(financingType)
        ? repaymentSchedule
        : null,
      equityNote: financingType === "equity" ? equityNote : "",
      grantConditions: financingType === "grant" ? grantConditions : "",
      arrangementFee: parseFloat(arrangementFee),
      legalFees: parseFloat(legalFees),
      description,
    };
    onComplete(eventData);
    onOpenChange(false);
    resetForm();
  };

  const resetForm = () => {
    setStep(1);
    setFinancingType("loan");
    setAmount("100000");
    setReceiveDate(new Date(2026, 2, 1));
    setInterestRate("5");
    setTermMonths("60");
    setRepaymentSchedule("monthly");
    setEquityNote("");
    setGrantConditions("");
    setArrangementFee("2000");
    setLegalFees("1500");
    setDescription("");
  };

  const calculateRepayment = () => {
    if (!["loan", "overdraft"].includes(financingType)) return 0;

    const principal = parseFloat(amount) || 0;
    const monthlyRate = parseFloat(interestRate) / 100 / 12;
    const numPayments =
      repaymentSchedule === "monthly"
        ? parseInt(termMonths)
        : parseInt(termMonths) / 3;

    if (monthlyRate === 0) return principal / numPayments;

    const payment =
      (principal * (monthlyRate * Math.pow(1 + monthlyRate, numPayments))) /
      (Math.pow(1 + monthlyRate, numPayments) - 1);
    return payment;
  };

  const calculateTotalRepayment = () => {
    if (!["loan", "overdraft"].includes(financingType)) return 0;
    const payment = calculateRepayment();
    const numPayments =
      repaymentSchedule === "monthly"
        ? parseInt(termMonths)
        : parseInt(termMonths) / 3;
    return payment * numPayments;
  };

  const calculateTotalCosts = () => {
    return (parseFloat(arrangementFee) || 0) + (parseFloat(legalFees) || 0);
  };

  const calculate12MonthImpact = () => {
    const cashIn = parseFloat(amount) || 0;
    const upfrontCosts = calculateTotalCosts();
    const netCashIn = cashIn - upfrontCosts;

    let cashOut12Months = 0;
    if (["loan", "overdraft"].includes(financingType)) {
      const payment = calculateRepayment();
      const paymentsIn12Months = repaymentSchedule === "monthly" ? 12 : 4;
      cashOut12Months = payment * paymentsIn12Months;
    }

    const netImpact = netCashIn - cashOut12Months;

    return { cashIn, upfrontCosts, netCashIn, cashOut12Months, netImpact };
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Secure Financing Event - Step {step} of 4</DialogTitle>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4">
            <h3 className="font-semibold">Basic Information</h3>

            <div className="space-y-2">
              <Label>Financing Type *</Label>
              <Select
                value={financingType}
                onValueChange={(val) => setFinancingType(val as any)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="loan">Loan</SelectItem>
                  <SelectItem value="overdraft">Overdraft</SelectItem>
                  <SelectItem value="equity">Equity Raise</SelectItem>
                  <SelectItem value="grant">Grant</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="amount">Amount *</Label>
              <div className="flex items-center gap-2">
                <span className="text-sm">£</span>
                <Input
                  id="amount"
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Receive Date *</Label>
              <DatePicker
                value={receiveDate}
                onChange={(date) => date && setReceiveDate(date)}
                dateFormat="PPP"
              />
              <p className="text-sm text-muted-foreground">
                When funds will be received
              </p>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button onClick={handleNext} disabled={!amount}>
                Next: Terms →
              </Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <h3 className="font-semibold">Terms & Conditions</h3>

            {["loan", "overdraft"].includes(financingType) && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="interestRate">Interest Rate *</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="interestRate"
                      type="number"
                      value={interestRate}
                      onChange={(e) => setInterestRate(e.target.value)}
                      className="w-32"
                      step="0.1"
                    />
                    <span className="text-sm">% per year</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="termMonths">Term *</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="termMonths"
                      type="number"
                      value={termMonths}
                      onChange={(e) => setTermMonths(e.target.value)}
                      className="w-32"
                    />
                    <span className="text-sm">months</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Repayment Schedule *</Label>
                  <Select
                    value={repaymentSchedule}
                    onValueChange={(val) =>
                      setRepaymentSchedule(val as "monthly" | "quarterly")
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="bg-muted p-4 rounded-lg space-y-2">
                  <p className="font-semibold text-sm">Repayment Summary:</p>
                  <p className="text-sm">
                    {repaymentSchedule === "monthly" ? "Monthly" : "Quarterly"}{" "}
                    payment: £
                    {calculateRepayment().toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </p>
                  <p className="text-sm">
                    Total repayment: £
                    {calculateTotalRepayment().toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Interest cost: £
                    {(
                      calculateTotalRepayment() - parseFloat(amount)
                    ).toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </p>
                </div>
              </>
            )}

            {financingType === "equity" && (
              <div className="space-y-2">
                <Label htmlFor="equityNote">Ownership Dilution / Notes</Label>
                <Textarea
                  id="equityNote"
                  value={equityNote}
                  onChange={(e) => setEquityNote(e.target.value)}
                  placeholder="e.g., 10% equity stake, no repayment required"
                  rows={3}
                />
                <p className="text-sm text-muted-foreground">
                  Note: Equity raises do not require repayment but dilute
                  ownership
                </p>
              </div>
            )}

            {financingType === "grant" && (
              <div className="space-y-2">
                <Label htmlFor="grantConditions">
                  Grant Conditions / Requirements
                </Label>
                <Textarea
                  id="grantConditions"
                  value={grantConditions}
                  onChange={(e) => setGrantConditions(e.target.value)}
                  placeholder="e.g., Must be used for R&D, reporting requirements"
                  rows={3}
                />
                <p className="text-sm text-muted-foreground">
                  Note: Grants do not require repayment but may have conditions
                </p>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={handleBack}>
                ← Back
              </Button>
              <Button onClick={handleNext}>Next: Costs →</Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <h3 className="font-semibold">Associated Costs</h3>
            <p className="text-sm text-muted-foreground">
              One-time costs to secure financing:
            </p>

            <div className="space-y-2">
              <Label htmlFor="arrangementFee">Arrangement Fee</Label>
              <div className="flex items-center gap-2">
                <span className="text-sm">£</span>
                <Input
                  id="arrangementFee"
                  type="number"
                  value={arrangementFee}
                  onChange={(e) => setArrangementFee(e.target.value)}
                />
              </div>
              <p className="text-sm text-muted-foreground">
                Typically 1-2% of loan amount
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="legalFees">Legal Fees</Label>
              <div className="flex items-center gap-2">
                <span className="text-sm">£</span>
                <Input
                  id="legalFees"
                  type="number"
                  value={legalFees}
                  onChange={(e) => setLegalFees(e.target.value)}
                />
              </div>
              <p className="text-sm text-muted-foreground">
                Legal and documentation costs
              </p>
            </div>

            <div className="bg-muted p-4 rounded-lg">
              <p className="text-sm font-semibold">Total One-Time Costs:</p>
              <p className="text-lg text-red-600">
                {formatCurrency(calculateTotalCosts(), "full")}
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                Net cash received:{" "}
                {formatCurrency(
                  parseFloat(amount) - calculateTotalCosts(),
                  "full",
                )}
              </p>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={handleBack}>
                ← Back
              </Button>
              <Button onClick={handleNext}>Next: Review →</Button>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-4">
            <h3 className="font-semibold">Review Event Summary</h3>

            <div className="space-y-2">
              <p className="font-semibold">
                {financingType === "loan"
                  ? "Loan"
                  : financingType === "overdraft"
                    ? "Overdraft"
                    : financingType === "equity"
                      ? "Equity Raise"
                      : "Grant"}
              </p>
              <p className="text-sm">
                Amount: {formatCurrency(parseFloat(amount), "full")}
              </p>
              <p className="text-sm">
                Receive Date: {format(receiveDate, "MMM d, yyyy")}
              </p>
              {["loan", "overdraft"].includes(financingType) && (
                <p className="text-sm">
                  Terms: {interestRate}% over {termMonths} months (
                  {repaymentSchedule} payments)
                </p>
              )}
            </div>

            <div className="border-t pt-4 space-y-3">
              <p className="font-semibold">
                ─── CASH FLOW IMPACT ───────────────────────────
              </p>

              <p className="font-semibold text-sm">
                Cash {getCashMovementTerms().in} (
                {format(receiveDate, "MMM yyyy")}):
              </p>
              <p className="text-sm text-green-600">
                • Financing received: +
                {formatCurrency(parseFloat(amount), "full")}
              </p>
              <p className="text-sm text-red-600">
                • Arrangement fee: -
                {formatCurrency(parseFloat(arrangementFee), "full")}
              </p>
              <p className="text-sm text-red-600">
                • Legal fees: -{formatCurrency(parseFloat(legalFees), "full")}
              </p>
              <p className="text-sm font-semibold">
                Net cash in: +
                {formatCurrency(calculate12MonthImpact().netCashIn, "full")}
              </p>

              {["loan", "overdraft"].includes(financingType) && (
                <>
                  <p className="font-semibold text-sm mt-4">
                    {repaymentSchedule === "monthly" ? "Monthly" : "Quarterly"}{" "}
                    Repayments ({format(addMonths(receiveDate, 1), "MMM yyyy")}{" "}
                    onwards):
                  </p>
                  <p className="text-sm text-red-600">
                    • Repayment: -£
                    {calculateRepayment().toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                    /{repaymentSchedule === "monthly" ? "month" : "quarter"}
                  </p>
                </>
              )}

              {financingType === "equity" && equityNote && (
                <div className="bg-blue-50 border border-blue-200 rounded p-3 mt-4">
                  <p className="text-sm text-blue-800">
                    <strong>Equity Terms:</strong> {equityNote}
                  </p>
                </div>
              )}

              {financingType === "grant" && grantConditions && (
                <div className="bg-blue-50 border border-blue-200 rounded p-3 mt-4">
                  <p className="text-sm text-blue-800">
                    <strong>Grant Conditions:</strong> {grantConditions}
                  </p>
                </div>
              )}

              <p className="font-semibold text-sm mt-4">
                ─── NET IMPACT (12 months) ──────────────────────
              </p>
              <p className="text-sm">
                Cash in: +
                {formatCurrency(calculate12MonthImpact().netCashIn, "full")}
              </p>
              {calculate12MonthImpact().cashOut12Months > 0 && (
                <p className="text-sm">
                  12-month repayments: -£
                  {calculate12MonthImpact().cashOut12Months.toLocaleString(
                    undefined,
                    { minimumFractionDigits: 2, maximumFractionDigits: 2 },
                  )}
                </p>
              )}
              <p className="text-sm font-semibold">
                Net 12-month impact: +£
                {calculate12MonthImpact().netImpact.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </p>

              {["loan", "overdraft"].includes(financingType) && (
                <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mt-4">
                  <p className="text-sm text-yellow-800">
                    ⚠️ Total interest cost over {termMonths} months: £
                    {(
                      calculateTotalRepayment() - parseFloat(amount)
                    ).toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </p>
                </div>
              )}

              {["equity", "grant"].includes(financingType) && (
                <div className="bg-green-50 border border-green-200 rounded p-3 mt-4">
                  <p className="text-sm text-green-800">
                    ✓ No repayment required - this is non-dilutive funding
                  </p>
                </div>
              )}
            </div>

            <div className="space-y-2 pt-4">
              <Label htmlFor="description">Event Description (optional):</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="e.g., Working capital loan to fund expansion"
                rows={2}
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={handleBack}>
                ← Back
              </Button>
              <Button onClick={handleComplete}>
                Add Event to {getScenarioTerm(true)}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
